import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, Card, Typography, Skeleton, Button, Avatar, IconButton } from "@mui/material";
import { Users, ArrowRight, UserPlus, Check, X, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { useAuth } from "../../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PersonHoverCard from "./PersonHoverCard";

const RELATIONS_API = "https://apiweb.hitpoly.com/ajax/RelacionesControlador.php";
const COMMUNITY_API = "https://apiweb.hitpoly.com/ajax/communityWidgetsController.php";
const AVATAR_CACHE = {};

const PEOPLE_CARD_WIDTH = 150;

const PeopleYouMayKnow = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarStatus, setAvatarStatus] = useState({});
  const [relationStatuses, setRelationStatuses] = useState({});
  const mountedRef = useRef(true);
  const userIdRef = useRef(null);
  const scrollRef = useRef(null);

  // Hover popup state
  const [hoverPerson, setHoverPerson] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const hoverTimer = useRef(null);

  const scrollLeft = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  userIdRef.current = user?.user_id || user?.id;

  const createSlug = useCallback((nombre, apellido) => {
    const cleanNombre = String(nombre || "").toLowerCase().trim().replace(/\s+/g, "");
    const cleanApellido = String(apellido || "").toLowerCase().trim().replace(/\s+/g, "");
    return cleanApellido ? `${cleanNombre}.${cleanApellido}` : cleanNombre;
  }, []);

  const goToProfile = useCallback((person) => {
    const nombre = person.nombre || person.name?.split(" ")[0] || "";
    const apellido = person.apellido || person.name?.split(" ").slice(1).join(" ") || "";
    const slug = createSlug(nombre, apellido);
    navigate(`/${slug}`, { state: { targetId: person.id } });
  }, [navigate, createSlug]);

  const checkAvatar = useCallback((url) => {
    if (!url) return Promise.resolve(false);
    if (url in AVATAR_CACHE) return Promise.resolve(AVATAR_CACHE[url]);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { AVATAR_CACHE[url] = true; resolve(true); };
      img.onerror = () => { AVATAR_CACHE[url] = false; resolve(false); };
      img.src = url;
    });
  }, []);

  const checkRelationStatus = useCallback(async (currentUserId, targetId) => {
    if (!currentUserId || !targetId) return { status: "none", isSender: false };
    try {
      const res = await axios.get(RELATIONS_API, {
        params: { action: "status", user_id: currentUserId, target_id: targetId }
      });
      if (res.data?.success) {
        return {
          status: res.data.status || "none",
          isSender: Number(res.data.remitente_original) === Number(currentUserId)
        };
      }
    } catch {}
    return { status: "none", isSender: false };
  }, []);

  const handleFriendAction = useCallback(async (person, action) => {
    const currentUserId = userIdRef.current;
    if (!currentUserId || !person.id) return;
    try {
      const res = await axios.post(RELATIONS_API, {
        user_id: currentUserId,
        target_id: person.id,
        action: action
      });
      if (res.data?.success && mountedRef.current) {
        setRelationStatuses(prev => ({
          ...prev,
          [person.id]: { status: res.data.newStatus, isSender: action === "enviar" }
        }));
        if (action === "enviar") {
          axios.post("https://apiweb.hitpoly.com/ajax/NotificacionesDeSolicitudDeAmistadController.php", {
            user_id: currentUserId,
            target_id: person.id,
            tipo: "enviar"
          }).catch(() => {});
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    const currentUserId = user?.user_id || user?.id;
    if (!currentUserId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchPeople = async () => {
      try {
        const res = await axios.post(COMMUNITY_API, {
          action: "getPeopleYouMayKnow",
          user_id: currentUserId
        }, { signal: controller.signal });

        if (cancelled) return;

        let data = [];
        if (res.data?.success && res.data.data?.length > 0) {
          data = res.data.data;
        } else if (res.data?.success && res.data.datos?.length > 0) {
          data = res.data.datos;
        }

        const items = data;
        
        if (data.length === 0) {
          if (mountedRef.current) setLoading(false);
          return;
        }

        const statuses = {};
        await Promise.all(data.map(async (person) => {
          statuses[person.id] = await checkAvatar(person.avatar);
        }));

        if (cancelled) return;

        const valid = data.filter(p => statuses[p.id]);
        const invalid = data.filter(p => !statuses[p.id]);
        const sorted = [...valid, ...invalid];

        const top25 = sorted.slice(0, 25);

        const relStatuses = {};
        await Promise.all(top25.map(async (person) => {
          if (Number(person.id) !== Number(currentUserId)) {
            relStatuses[person.id] = await checkRelationStatus(currentUserId, person.id);
          }
        }));

        if (mountedRef.current && !cancelled) {
          setAvatarStatus(statuses);
          setPeople(top25);
          setRelationStatuses(relStatuses);
          setLoading(false);
        }
      } catch (err) {
        if (mountedRef.current) setLoading(false);
      }
    };

    fetchPeople();

    return () => { cancelled = true; controller.abort(); };
  }, []);

  if (loading) return <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 4 }} />;
  if (!people || people.length === 0) return null;

  const getDisplayName = (person) => {
    if (person.nombre && person.apellido) return `${person.nombre} ${person.apellido}`;
    return person.name || "Usuario";
  };

  const getFirstName = (person) => {
    return person.nombre || person.name?.split(" ")[0] || "";
  };

  const getRole = (person) => {
    const loc = [person.city, person.country].filter(Boolean).join(', ');
    if (loc) return loc;
    return person.cargo_nombre || person.role || "Profesional en Hitpoly";
  };

  const handleMouseEnter = (e, person) => {
    clearTimeout(hoverTimer.current);
    const target = e.currentTarget;
    hoverTimer.current = setTimeout(() => {
      setHoverPerson(person);
      setHoverAnchor(target);
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      setHoverPerson(null);
      setHoverAnchor(null);
    }, 300);
  };

  const renderFriendButton = (person) => {
    if (Number(person.id) === Number(userIdRef.current)) return null;

    const rel = relationStatuses[person.id];

    if (!rel || rel.status === "none") {
      return (
        <Button
          size="small"
          variant="contained"
          sx={{ width: '100%', textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, boxShadow: 'none', minHeight: 26, bgcolor: '#0a66c2' }}
          startIcon={<UserPlus size={12} />}
          onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "enviar"); }}
        >
          Conectar
        </Button>
      );
    }

    if (rel.status === "pending") {
      if (rel.isSender) {
        return (
          <Button
            size="small"
            variant="outlined"
            sx={{ width: '100%', textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, minHeight: 26, borderColor: '#e0e0e0', color: '#666' }}
            startIcon={<X size={12} />}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "eliminar"); }}
          >
            Cancelar
          </Button>
        );
      }
      return (
        <Box sx={{ display: 'flex', gap: 0.5, width: '100%' }}>
          <Button
            size="small"
            variant="contained"
            sx={{ flex: 1, minWidth: 0, textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, boxShadow: 'none', minHeight: 26, bgcolor: '#0a66c2' }}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "aceptar"); }}
          >
            <Check size={12} />
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ flex: 1, minWidth: 0, textTransform: 'none', borderRadius: 2, fontSize: '0.7rem', py: 0.4, minHeight: 26, borderColor: '#e0e0e0', color: '#666' }}
            onClick={(e) => { e.stopPropagation(); handleFriendAction(person, "eliminar"); }}
          >
            <X size={12} />
          </Button>
        </Box>
      );
    }

    if (rel.status === "accepted") {
      return null;
    }

    return null;
  };

  return (
    <Card sx={{ 
      bgcolor: "rgba(255,255,255,0.9)", 
      backdropFilter: "blur(8px)", 
      borderRadius: 4, 
      border: '1px solid #f0f0f0',
      boxShadow: 'none',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 300
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="800" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
          <Users size={18} color="#0a66c2" /> Personas
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={scrollLeft} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronLeft size={16} /></IconButton>
          <IconButton size="small" onClick={scrollRight} sx={{ bgcolor: '#f0f2f5', width: 28, height: 28 }}><ChevronRight size={16} /></IconButton>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ 
        display: 'flex', 
        gap: 2, 
        overflowX: 'auto', className: 'community-wall-scroll', scrollbarWidth: 'thin', scrollbarColor: '#0B8DB5 transparent', 
        pb: 1.5,
        mx: -0.5,
        px: 0.5,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#0B8DB5', borderRadius: 4, border: 0 }
      }}>
        {people.map(person => (
          <Card key={person.id}
            onMouseEnter={(e) => handleMouseEnter(e, person)}
            onMouseLeave={handleMouseLeave}
            sx={{ 
              minWidth: PEOPLE_CARD_WIDTH, 
              maxWidth: PEOPLE_CARD_WIDTH,
              p: 2, 
              borderRadius: 3, 
              border: '1px solid #eef3f8',
              boxShadow: 'none',
              bgcolor: '#ffffff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              '&:hover': {
                borderColor: '#0a66c2',
                boxShadow: '0 4px 12px rgba(10, 102, 194, 0.08)'
              }
            }}>
            <Box onClick={() => goToProfile(person)} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 1 }}>
              <Avatar 
                src={avatarStatus[person.id] ? person.avatar : undefined}
                sx={{ width: 64, height: 64, mb: 1.5, border: '2px solid #fff', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', bgcolor: '#e0e0e0', color: '#0a66c2', fontWeight: 'bold', fontSize: '1.3rem' }}
              >
                {getFirstName(person).charAt(0)}
              </Avatar>
              <Typography variant="body2" fontWeight="800" color="#1a1a1a" sx={{ mb: 0.5, lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', width: '100%' }}>
                {getDisplayName(person)}
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight="500" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', width: '100%', alignItems: 'center', justifyContent: 'center' }}>
                {(person.country || person.city) && <MapPin size={10} style={{ marginRight: 3, display: 'inline' }} />}
                {getRole(person)}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>

        <Button 
          onClick={() => navigate('/red-personas')}
          sx={{ 
            width: '100%', 
            mt: 1, 
            textTransform: 'none', 
            fontWeight: '700',
            color: '#0a66c2',
            '&:hover': { bgcolor: '#eef3f8' }
          }}
          endIcon={<ArrowRight size={16} />}
        >
          Ver toda la red
        </Button>

      {/* Popup hover */}
      {hoverPerson && hoverAnchor && (
        <PersonHoverCard
          person={hoverPerson}
          anchorEl={hoverAnchor}
          currentUserId={userIdRef.current}
          relationStatus={relationStatuses[hoverPerson.id]}
          onClose={() => { setHoverPerson(null); setHoverAnchor(null); }}
          onMouseEnter={() => { clearTimeout(hoverTimer.current); }}
          onMouseLeave={handleMouseLeave}
          onFriendAction={(action) => handleFriendAction(hoverPerson, action)}
          onNavigateProfile={() => { setHoverPerson(null); goToProfile(hoverPerson); }}
          onNavigateMessage={() => {
            setHoverPerson(null);
            const nombre = hoverPerson.nombre || hoverPerson.name?.split(" ")[0] || "";
            const apellido = hoverPerson.apellido || hoverPerson.name?.split(" ").slice(1).join(" ") || "";
            window.postMessage({
              type: 'OPEN_FLOATING_CHAT',
              partner: {
                id: hoverPerson.id,
                name: `${nombre} ${apellido}`.trim(),
                avatar: hoverPerson.avatar || '',
                type: 'professional'
              }
            }, '*');
          }}
        />
      )}
    </Card>
  );
};

export default PeopleYouMayKnow;


